import React, { useState, useMemo } from "react";
import { base44 } from "@/race-tracker/api/rtClient";
import { useQuery } from "@tanstack/react-query";
import AnalyticsFilters from "../components/analytics/AnalyticsFilters";
import StatCards from "../components/analytics/StatCards";
import AnalystChart from "../components/analytics/AnalystChart";
import AgencyChart from "../components/analytics/AgencyChart";
import AccountTeamChart from "../components/analytics/AccountTeamChart";
import SweepStalenessTable from "../components/analytics/SweepStalenessTable";
import RaceDetailModal from "../components/modals/RaceDetailModal";
import { useQueryClient } from "@tanstack/react-query";

const DEFAULT_FILTERS = {
  status: ["Active"],
  agency: ["SMG", "DCM", "DRM", "SMG/DCM", "No Client"],
  da: "all",
  staleness_days: 7,
};

export default function Analytics() {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [selectedRace, setSelectedRace] = useState(null);

  const { data: advertisers = [] } = useQuery({
    queryKey: ["advertisers"],
    queryFn: () => base44.entities.Advertiser.list("-created_date", 500),
  });

  const { data: races = [] } = useQuery({
    queryKey: ["races"],
    queryFn: () => base44.entities.Race.list("-created_date", 5000),
  });

  const adsMap = useMemo(() => {
    const map = {};
    advertisers.forEach((a) => { map[a.id] = a; });
    return map;
  }, [advertisers]);

  // Join races with advertiser data
  const enrichedRaces = useMemo(() => {
    return races.map((r) => {
      const ad = adsMap[r.advertiser_id];
      if (!ad) return null;
      return { ...r, _ad: ad };
    }).filter(Boolean);
  }, [races, adsMap]);

  // Apply filters
  const filteredRaces = useMemo(() => {
    return enrichedRaces.filter((r) => {
      if (filters.status.length > 0 && !filters.status.includes(r.status)) return false;
      if (filters.agency.length > 0 && !filters.agency.includes(r._ad.agency)) return false;
      if (filters.da !== "all" && r._ad.data_analyst !== filters.da) return false;
      return true;
    });
  }, [enrichedRaces, filters]);

  const filteredAdvertisers = useMemo(() => {
    const seen = new Set();
    const result = [];
    filteredRaces.forEach((r) => {
      if (!seen.has(r._ad.id)) {
        seen.add(r._ad.id);
        result.push(r._ad);
      }
    });
    return result;
  }, [filteredRaces]);

  const today = new Date();
  const isOverdue = (dateStr) => {
    if (!dateStr) return true;
    const days = Math.floor((today - new Date(dateStr)) / (1000 * 60 * 60 * 24));
    return days > filters.staleness_days;
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold tracking-tight">Analytics</h1>
        <p className="text-xs text-muted-foreground mt-0.5">Workload distribution, coverage, and sweep health</p>
      </div>

      <AnalyticsFilters filters={filters} onChange={setFilters} />

      <StatCards
        races={filteredRaces}
        advertisers={filteredAdvertisers}
        stalenessThreshold={filters.staleness_days}
        isOverdue={isOverdue}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <AnalystChart races={filteredRaces} />
        <AgencyChart races={filteredRaces} />
        <AccountTeamChart races={filteredRaces} />
      </div>

      <SweepStalenessTable
        races={filteredRaces}
        stalenessThreshold={filters.staleness_days}
        isOverdue={isOverdue}
        onRaceClick={setSelectedRace}
      />

      {selectedRace && (
        <RaceDetailModal
          race={selectedRace}
          open={!!selectedRace}
          onClose={() => setSelectedRace(null)}
          onSave={() => {
            setSelectedRace(null);
            queryClient.invalidateQueries({ queryKey: ["races"] });
          }}
          onDelete={async (id) => {
            await base44.entities.Race.delete(id);
            setSelectedRace(null);
            queryClient.invalidateQueries({ queryKey: ["races"] });
          }}
        />
      )}
    </div>
  );
}